import { useState } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import { castingStyleData, BLACK_MAGIC_PROGRESSION } from "../../../../../SharedData/data";
import { useTheme } from "../../../../../contexts/ThemeContext";

const CastingStyleCard = ({
  styleName,
  data,
  isSelected,
  onSelect,
  isExpanded,
  onToggleExpand,
}) => {
  const { theme } = useTheme();
  return (
    <div
      style={{
        border: `2px solid ${isSelected ? data.color : theme.border}`,
        borderRadius: "12px",
        padding: "16px",
        marginBottom: "12px",
        backgroundColor: isSelected ? `${data.color}10` : theme.background,
        cursor: "pointer",
        width: "100%",
        transition: "all 0.2s ease",
        boxShadow: isSelected
          ? `0 4px 12px ${data.color}20`
          : "0 2px 4px rgba(0,0,0,0.1)",
      }}
      onClick={onSelect}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <input
            type="radio"
            name="castingStyle"
            value={styleName}
            checked={isSelected}
            onChange={() => onSelect()}
            style={{
              width: "18px",
              height: "18px",
              accentColor: theme.primary,
              cursor: "pointer",
            }}
          />
          <div>
            <h3
              style={{
                margin: 0,
                fontSize: "18px",
                fontWeight: "600",
                color: theme.text,
              }}
            >
              {styleName}
            </h3>
            <p
              style={{
                margin: "4px 0 0 0",
                fontSize: "14px",
                color: theme.textSecondary,
                lineHeight: "1.4",
              }}
            >
              {data.description}
            </p>
          </div>
        </div>
        <button
          onClick={(e) => {
            e.stopPropagation();
            onToggleExpand();
          }}
          style={{
            background: "none",
            border: "none",
            cursor: "pointer",
            padding: "4px",
            borderRadius: "4px",
            color: data.color,
          }}
        >
          {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
        </button>
      </div>

      <div
        style={{
          display: "flex",
          gap: "16px",
          marginTop: "12px",
          flexWrap: "wrap",
        }}
      >
        <div style={{ fontSize: "12px", color: theme.text }}>
          <strong>Hit Die:</strong> {data.hitDie}
        </div>
        <div style={{ fontSize: "12px", color: theme.text }}>
          <strong>Spellcasting:</strong> {data.spellcastingAbility}
        </div>
        <div style={{ fontSize: "12px", color: theme.text }}>
          <strong>Base AC:</strong> {data.baseAC}
        </div>
      </div>

      {isExpanded && (
        <div
          style={{
            marginTop: "16px",
            borderTop: `1px solid ${theme.border}`,
            paddingTop: "16px",
          }}
        >
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
              gap: "16px",
            }}
          >
            <div>
              <h4
                style={{
                  margin: "0 0 8px 0",
                  fontSize: "14px",
                  fontWeight: "600",
                  color: data.color,
                }}
              >
                Hit Points
              </h4>
              <div
                style={{
                  fontSize: "12px",
                  color: theme.text,
                  lineHeight: "1.5",
                }}
              >
                <div>
                  <strong>1st Level:</strong> {data.hitPointsAtFirst}
                </div>
                <div>
                  <strong>Higher Levels:</strong> {data.hitPointsPerLevel}
                </div>
              </div>
            </div>

            <div>
              <h4
                style={{
                  margin: "0 0 8px 0",
                  fontSize: "14px",
                  fontWeight: "600",
                  color: data.color,
                }}
              >
                Proficiencies
              </h4>
              <div
                style={{
                  fontSize: "12px",
                  color: theme.textSecondary,
                  lineHeight: "1.5",
                }}
              >
                <div>
                  <strong>Saving Throws:</strong> {data.savingThrows.join(", ")}
                </div>
                <div>
                  <strong>Skills:</strong> Choose 2 from {data.skills.length}{" "}
                  options
                </div>
              </div>
            </div>

            <div style={{ gridColumn: "1 / -1" }}>
              <h4
                style={{
                  margin: "0 0 8px 0",
                  fontSize: "14px",
                  fontWeight: "600",
                  color: data.color,
                }}
              >
                Key Features
              </h4>
              <ul
                style={{
                  margin: 0,
                  paddingLeft: "16px",
                  fontSize: "12px",
                  color: theme.textSecondary,
                  lineHeight: "1.5",
                }}
              >
                {data.keyFeatures.map((feature, index) => (
                  <li key={index} style={{ marginBottom: "4px" }}>
                    <strong>{feature.name}</strong> (Level {feature.level}):{" "}
                    {feature.description}
                  </li>
                ))}
              </ul>
            </div>

            <div style={{ gridColumn: "1 / -1" }}>
              <h4
                style={{
                  margin: "0 0 8px 0",
                  fontSize: "14px",
                  fontWeight: "600",
                  color: data.color,
                }}
              >
                Available Skills
              </h4>
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: "6px",
                }}
              >
                {data.skills.map((skill, index) => (
                  <span
                    key={index}
                    style={{
                      backgroundColor: `${data.color}15`,
                      color: data.color,
                      padding: "4px 8px",
                      borderRadius: "4px",
                      fontSize: "11px",
                      fontWeight: "500",
                    }}
                  >
                    {skill}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export const EnhancedCastingStyleSelector = ({
  selectedStyle = "",
  onStyleChange = () => {},
  required = false,
  character = null,
  setCharacter = () => {},
}) => {
  const { theme } = useTheme();
  const [expandedCard, setExpandedCard] = useState(null);

  const level = character?.level || 1;

  const handleCardSelect = (styleName) => {
    onStyleChange(styleName);

    setExpandedCard(styleName);
  };

  const handleToggleExpand = (styleName) => {
    setExpandedCard(expandedCard === styleName ? null : styleName);
  };

  const getCastingStyleFeatures = () => {
    if (!selectedStyle || !castingStyleData[selectedStyle]) {
      return [];
    }

    const styleData = castingStyleData[selectedStyle];
    const features = [];

    styleData.keyFeatures?.forEach((feature) => {
      if (level >= feature.level) {
        if (
          selectedStyle === "Intellect Caster" &&
          feature.name === "Tactical Wit"
        ) {
          const dexScore = character?.abilityScores?.dexterity || 10;
          const intScore = character?.abilityScores?.intelligence || 10;
          const dexMod = Math.floor((dexScore - 10) / 2);
          const intMod = Math.floor((intScore - 10) / 2);
          const formatMod = (mod) => (mod >= 0 ? `+${mod}` : `${mod}`);

          features.push({
            name: "Initiative Ability",
            level: 1,
            description:
              "As an Intellect Caster, you may choose to use Intelligence or Dexterity for initiative (from Tactical Wit feature).",
            isChoice: true,
            choices: [
              {
                name: "Dexterity",
                description: `Use Dexterity for initiative rolls (${formatMod(
                  dexMod
                )} base modifier).`,
              },
              {
                name: "Intelligence",
                description: `Use Intelligence for initiative rolls (${formatMod(
                  intMod
                )} base modifier).`,
              },
            ],
            choiceKey: "initiativeAbility",
          });
        }

        if (
          selectedStyle === "Willpower Caster" &&
          feature.name === "Black Magic"
        ) {
          const blackMagicDamage = BLACK_MAGIC_PROGRESSION[level] || "1d6";
          features.push({
            name: feature.name,
            level: feature.level,
            description: `Once per turn, you can deal an extra ${blackMagicDamage} damage to one creature you hit with a cantrip if you have advantage on the attack roll. You don't need advantage if another enemy of the target is within 5 feet of it, that enemy isn't incapacitated, and you don't have disadvantage on the attack roll.`,
            automatic: true,
          });
        } else if (feature.isChoice && feature.choices) {
          features.push({
            name: feature.name,
            level: feature.level,
            description: feature.description,
            isChoice: true,
            choices: feature.choices,
            choiceKey:
              feature.name === "Black Magic Specialization"
                ? "blackMagicSpecialization"
                : undefined,
          });
        } else {
          features.push({
            name: feature.name,
            level: feature.level,
            description: feature.description,
            automatic: !feature.isChoice,
          });
        }
      }
    });

    return features;
  };

  const handleChoiceChange = (choiceKey, selectedChoice) => {
    const currentChoices = character.castingStyleChoices || {};
    const updatedChoices = {
      ...currentChoices,
      [choiceKey]: selectedChoice,
    };
    setCharacter("castingStyleChoices", updatedChoices);

    if (choiceKey === "initiativeAbility") {
      setCharacter("initiativeAbility", selectedChoice.toLowerCase());
    }
  };

  const features = getCastingStyleFeatures();

  return (
    <div style={{ width: "100%" }}>
      <div style={{ marginBottom: "16px" }}>
        <label
          style={{
            display: "block",
            fontSize: "16px",
            fontWeight: "600",
            color: theme.text,
            marginBottom: "8px",
          }}
        >
          Casting Style {required && "*"}
        </label>
        <p
          style={{
            fontSize: "14px",
            color: theme.textSecondary,
            margin: "0 0 16px 0",
            lineHeight: "1.5",
          }}
        >
          Choose your magical approach. Each casting style has unique abilities,
          spellcasting methods, and strengths. Click on a card to select it, or
          use the arrow to expand details.
        </p>
      </div>

      <div>
        {Object.entries(castingStyleData).map(([styleName, data]) => (
          <CastingStyleCard
            key={styleName}
            styleName={styleName}
            data={data}
            isSelected={selectedStyle === styleName}
            onSelect={() => handleCardSelect(styleName)}
            isExpanded={expandedCard === styleName}
            onToggleExpand={() => handleToggleExpand(styleName)}
          />
        ))}
      </div>

      {selectedStyle && features.length > 0 && (
        <div
          style={{
            marginTop: "24px",
            padding: "20px",
            backgroundColor: theme.background,
            border: `1px solid ${theme.border}`,
            borderRadius: "8px",
          }}
        >
          <h3
            style={{
              color: theme.text,
              marginBottom: "20px",
              fontSize: "18px",
              fontWeight: "600",
            }}
          >
            {selectedStyle} Features (Level {level})
          </h3>

          {features.map((feature, index) => (
            <div
              key={index}
              style={{
                marginBottom: "20px",
                padding: "16px",
                backgroundColor: theme.background,
                border: `1px solid ${theme.border}`,
                borderRadius: "8px",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "8px",
                }}
              >
                <h4
                  style={{
                    color: theme.text,
                    margin: 0,
                    fontSize: "16px",
                    fontWeight: "600",
                  }}
                >
                  {feature.name}
                </h4>
                <span
                  style={{
                    color: theme.textSecondary,
                    fontSize: "12px",
                    fontWeight: "500",
                    backgroundColor: theme.surface,
                    padding: "2px 8px",
                    borderRadius: "4px",
                  }}
                >
                  Level {feature.level}
                </span>
              </div>

              <p
                style={{
                  color: theme.textSecondary,
                  margin: "0 0 12px 0",
                  fontSize: "14px",
                  lineHeight: "1.5",
                }}
              >
                {feature.description}
              </p>

              {feature.automatic && (
                <div
                  style={{
                    marginTop: "8px",
                    fontSize: "12px",
                    color: theme.success,
                    fontStyle: "italic",
                  }}
                >
                  ✓ Automatically available
                </div>
              )}

              {feature.isChoice && feature.choices && (
                <div style={{ marginTop: "16px" }}>
                  <div
                    style={{
                      marginBottom: "12px",
                      fontSize: "14px",
                      fontWeight: "600",
                      color: theme.text,
                    }}
                  >
                    {(() => {
                      const savedChoice =
                        character?.castingStyleChoices?.[feature.choiceKey];

                      const fallbackChoice =
                        feature.choiceKey === "initiativeAbility"
                          ? character?.initiativeAbility
                          : null;
                      const displayChoice =
                        savedChoice ||
                        (fallbackChoice
                          ? fallbackChoice.charAt(0).toUpperCase() +
                            fallbackChoice.slice(1)
                          : null);

                      return displayChoice
                        ? `Selected: ${displayChoice}`
                        : "Choose one option:";
                    })()}
                  </div>

                  {feature.choiceType === "initiativeAbility" &&
                    character?.abilityScores &&
                    character.abilityScores.dexterity &&
                    character.abilityScores.intelligence && (
                      <div
                        style={{
                          marginBottom: "12px",
                          padding: "8px 12px",
                          backgroundColor: `${theme.primary}10`,
                          border: `1px solid ${theme.primary}`,
                          borderRadius: "6px",
                          fontSize: "12px",
                          fontStyle: "italic",
                          color: theme.primary,
                        }}
                      >
                        {Math.floor(
                          (character.abilityScores.intelligence - 10) / 2
                        ) >
                        Math.floor((character.abilityScores.dexterity - 10) / 2)
                          ? "💡 Intelligence gives a higher modifier"
                          : Math.floor(
                              (character.abilityScores.dexterity - 10) / 2
                            ) >
                            Math.floor(
                              (character.abilityScores.intelligence - 10) / 2
                            )
                          ? "⚡ Dexterity gives a higher modifier"
                          : "⚖️ Both abilities give the same modifier"}
                      </div>
                    )}

                  {feature.choices.map((choice, choiceIndex) => {
                    const savedChoice =
                      character?.castingStyleChoices?.[feature.choiceKey];

                    const fallbackChoice =
                      feature.choiceKey === "initiativeAbility"
                        ? character?.initiativeAbility
                        : null;
                    const currentChoice =
                      savedChoice ||
                      (fallbackChoice
                        ? fallbackChoice.charAt(0).toUpperCase() +
                          fallbackChoice.slice(1)
                        : null);
                    const isSelected = currentChoice === choice.name;

                    return (
                      <div
                        key={choiceIndex}
                        style={{
                          marginBottom: "8px",
                          padding: "12px",
                          backgroundColor: isSelected
                            ? `${theme.primary}20`
                            : theme.surface,
                          border: `1px solid ${
                            isSelected ? theme.primary : theme.border
                          }`,
                          borderRadius: "6px",
                          cursor: "pointer",
                          transition: "all 0.2s ease",
                        }}
                        onClick={() =>
                          handleChoiceChange(
                            feature.choiceKey,
                            choice.value || choice.name,
                            feature.choiceType
                          )
                        }
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            marginBottom: "4px",
                          }}
                        >
                          <div
                            style={{
                              width: "16px",
                              height: "16px",
                              borderRadius: "50%",
                              border: `2px solid ${
                                isSelected ? theme.primary : theme.border
                              }`,
                              backgroundColor: isSelected
                                ? theme.primary
                                : "transparent",
                              marginRight: "8px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            {isSelected && (
                              <div
                                style={{
                                  width: "6px",
                                  height: "6px",
                                  borderRadius: "50%",
                                  backgroundColor: "white",
                                }}
                              />
                            )}
                          </div>
                          <span
                            style={{
                              fontWeight: "600",
                              fontSize: "14px",
                              color: isSelected ? theme.primary : theme.text,
                            }}
                          >
                            {choice.name}
                          </span>
                        </div>
                        <p
                          style={{
                            margin: "0 0 0 24px",
                            fontSize: "13px",
                            color: theme.textSecondary,
                            lineHeight: "1.4",
                          }}
                        >
                          {choice.description}
                        </p>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default EnhancedCastingStyleSelector;
